import { useRef, useState } from "react";
import { api } from "../api";
import type { Attachment } from "../types";
import { IconClip } from "./Icons";

/**
 * The files attached to the question being typed.
 *
 * Deliberately a row of chips rather than a preview pane: what a lawyer
 * needs to know before sending is which files are going, whether each one
 * could be read, and how to take one back. A thumbnail answers none of those.
 *
 * Split into a hook and two pieces because the design puts the paperclip in
 * the composer's bottom row and the chips above the text, and both need the
 * same in-flight uploads.
 */

const ACCEPT = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
].join(",");

/** A pending upload, before the server has given it an id. */
interface Uploading {
  key: string;
  filename: string;
}

function label(a: Attachment): string {
  if (a.status === "failed") return a.failure ?? "nu a putut fi citit";
  if (a.kind === "image") return "imagine";
  if (!a.pages) return "";
  // A .docx page count is derived from a character divisor, because
  // pagination does not exist until something renders the file. "≈4 pagini"
  // is the honest rendering of a number we worked out.
  const approx = a.page_basis === "docx" ? "≈" : "";
  return `${approx}${a.pages} ${a.pages === 1 ? "pagină" : "pagini"}`;
}

export function useUploads({
  attached,
  conversationId,
  onChange,
  onError,
}: {
  attached: Attachment[];
  conversationId: string | null;
  onChange: (next: Attachment[]) => void;
  onError: (message: string) => void;
}) {
  const [uploading, setUploading] = useState<Uploading[]>([]);
  const input = useRef<HTMLInputElement>(null);

  // The current list, readable from inside the upload loop without
  // depending on a value captured at render: two files chosen at once would
  // otherwise each overwrite the other's result.
  const latest = useRef(attached);
  latest.current = attached;

  async function add(files: FileList | null) {
    if (!files?.length) return;
    // Copied before the input is reset: `files` is the input's live
    // FileList, and clearing `value` empties it in place.
    const chosen = Array.from(files);
    if (input.current) input.current.value = "";

    for (const file of chosen) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      setUploading((u) => [...u, { key, filename: file.name }]);
      try {
        const done = await api.upload(file, conversationId);
        onChange([...latest.current, done]);
      } catch (err) {
        onError(err instanceof Error ? err.message : `Nu am putut încărca ${file.name}.`);
      } finally {
        setUploading((u) => u.filter((item) => item.key !== key));
      }
    }
  }

  async function remove(id: string) {
    onChange(attached.filter((a) => a.id !== id));
    try {
      await api.deleteUpload(id);
    } catch {
      // Already off the question, which is what the click meant. A failed
      // delete is the server's to sweep up.
    }
  }

  return {
    uploading,
    busy: uploading.length > 0,
    pick: () => input.current?.click(),
    remove,
    input: (
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        multiple
        hidden
        onChange={(e) => void add(e.target.files)}
      />
    ),
  };
}

export function AttachButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      className="icon-btn"
      disabled={disabled}
      onClick={onClick}
      aria-label="Atașează un document"
      title="Atașați un document sau o imagine"
    >
      <IconClip />
    </button>
  );
}

export function AttachmentChips({
  attached,
  uploading,
  onRemove,
}: {
  attached: Attachment[];
  uploading: Uploading[];
  onRemove: (id: string) => void;
}) {
  if (!attached.length && !uploading.length) return null;
  return (
    <div className="row">
      {attached.map((a) => (
        <span key={a.id} className={a.status === "failed" ? "attchip bad" : "attchip"}>
          <span className="att-name">{a.filename}</span>
          {label(a) && <span className="att-meta">{label(a)}</span>}
          <button type="button" onClick={() => onRemove(a.id)} aria-label={`Elimină ${a.filename}`}>
            ×
          </button>
        </span>
      ))}
      {uploading.map((u) => (
        <span key={u.key} className="attchip pending">
          <span className="att-name">{u.filename}</span>
          <span className="att-meta">se încarcă…</span>
        </span>
      ))}
    </div>
  );
}

/** The same chips, read-only, under a question in the transcript. */
export function AttachmentList({ items }: { items: Attachment[] }) {
  if (!items.length) return null;
  return (
    <span className="q-files">
      {items.map((a) => (
        <span key={a.id} className="q-file" title={label(a)}>
          {a.filename}
        </span>
      ))}
    </span>
  );
}
